import { useState } from 'react';
import { toast } from 'sonner';
import { Check, Link } from 'lucide-react';
import { Button } from '../button/button';
import { copyPageUrlToClipboard } from '../../../lib/utils/copy-page-url';
import { cn } from '../../../lib/utils';
import type { CopyLinkButtonProps } from './types';

const DEFAULT_TOOLTIP = 'Copy link';
const DEFAULT_COPIED_TOOLTIP = 'Link Copied!';
const COPIED_RESET_MS = 2000;

export function CopyLinkButton({
  path,
  tooltipText = DEFAULT_TOOLTIP,
  copiedTooltipText = DEFAULT_COPIED_TOOLTIP,
  className,
  onClick,
}: CopyLinkButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyPageUrlToClipboard(path)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_RESET_MS);
        onClick?.(e);
      })
      .catch((error) => {
        console.error('Error copying link:', error);
        toast.error('Failed to copy link. Please try again.');
      });
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        'h-8 w-8 p-0 text-prompt-safecomabbey hover:text-prompt-safecomoutrageous-orange',
        className,
      )}
      onClick={handleClick}
      tooltipContent={copied ? copiedTooltipText : tooltipText}
      preventClose
    >
      <span className="relative inline-flex h-4 w-4 items-center justify-center">
        <Link
          className={cn(
            'absolute h-4 w-4 transition-all duration-200 ease-out',
            copied ? 'scale-75 opacity-0' : 'scale-100 opacity-100',
          )}
          aria-hidden
        />
        <Check
          className={cn(
            'absolute h-4 w-4 transition-all duration-200 ease-out',
            copied ? 'scale-100 opacity-100' : 'scale-75 opacity-0',
          )}
          aria-hidden
        />
      </span>
    </Button>
  );
}
